<section class="text-center py-5 bg-body-tertiary">
  <div class="container">
    <div class="card border-0 shadow p-4 mx-auto">
      <div class="card-body">

        <img
          src="assets/images/sailboat-home.svg"
          alt="Sailboat illustration"
          class="img-fluid sailboat mx-auto d-block"
          style="max-width: 500px;"
        >
        <h2 class="fw-light mb-4 text-yachtapp">{{ slogan }}</h2>
        <p class="fw-normal text-black-50 mb-3">Your all-in-one yacht maintenance, log and service platform</p>
        <hr class="m-5">
        <div class="d-flex flex-column flex-sm-row justify-content-center gap-2">
          <a routerLink="/register" class="btn btn-lg btn-primary">Start Now!</a>
          <a routerLink="/subscriptions" class="btn btn-lg btn-outline-primary">See Plans</a>
        </div>
      </div>
    </div>
  </div>
</section>

<div class="porto-home">
  <section class="porto-hero position-relative overflow-hidden">
    <div class="container position-relative">
      <div class="row align-items-center g-5">
        <div class="col-lg-7">
          <span class="porto-eyebrow">Professional yacht operations, without enterprise complexity</span>
          <h1 class="display-3 fw-bold text-white mt-3 mb-4">
            Yacht management software for boat owners and charter fleets.
          </h1>
          <p class="lead text-white-50 mb-4 porto-hero-copy">
            {{ title }} brings structure, speed, and operational clarity to private boat ownership and growing charter businesses.
            Replace scattered notes, reactive repairs, and fragile handoffs with one polished system for maintenance, readiness,
            onboard coordination, documents, routes, and day-to-day control.
          </p>
          <div class="d-flex flex-column flex-md-row gap-3 mb-4">
            <a routerLink="/register" class="btn btn-light btn-lg porto-btn px-4">Launch your workspace</a>
            <a routerLink="/subscriptions" class="btn btn-outline-light btn-lg porto-btn px-4">Compare subscription plans</a>
          </div>
          <div class="row g-3 porto-mini-proof">
            <div class="col-sm-4">
              <div class="proof-chip">
                <strong>More control</strong>
                <span>See what matters before it becomes urgent.</span>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="proof-chip">
                <strong>Sharper service</strong>
                <span>Create smoother owner and guest experiences.</span>
              </div>
            </div>
            <div class="col-sm-4">
              <div class="proof-chip">
                <strong>Less friction</strong>
                <span>Keep crew, vendors, and timelines aligned.</span>
              </div>
            </div>
          </div>
        </div>

        <div class="col-lg-5">
          <div class="hero-panel box-shadow-4">
            <div class="hero-panel-top d-flex align-items-center justify-content-between mb-4">
              <span class="badge porto-badge">Operational readiness</span>
              <span class="hero-panel-status"><i class="bi bi-check2-circle"></i> Active</span>
            </div>

            <div class="hero-highlight-card mb-3">
              <div class="d-flex align-items-start gap-3">
                <div class="icon-box-circle bg-primary-subtle text-primary">
                  <i class="bi bi-tools"></i>
                </div>
                <div>
                  <h3 class="h5 mb-2">Maintenance that stays ahead of the season</h3>
                  <p class="mb-0 text-muted">
                    Track service intervals, worklogs, reminders, and vessel history in one place so critical tasks stop slipping through the cracks.
                  </p>
                </div>
              </div>
            </div>

            <div class="hero-highlight-card mb-3">
              <div class="d-flex align-items-start gap-3">
                <div class="icon-box-circle bg-info-subtle text-info-emphasis">
                  <i class="bi bi-map"></i>
                </div>
                <div>
                  <h3 class="h5 mb-2">Voyages, routes, and people fully connected</h3>
                  <p class="mb-0 text-muted">
                    Move from planning to execution with better route visibility, cleaner passenger coordination, and tighter cruise preparation.
                  </p>
                </div>
              </div>
            </div>

            <div class="hero-highlight-card">
              <div class="d-flex align-items-start gap-3">
                <div class="icon-box-circle bg-success-subtle text-success-emphasis">
                  <i class="bi bi-building-check"></i>
                </div>
                <div>
                  <h3 class="h5 mb-2">A more professional standard across every handoff</h3>
                  <p class="mb-0 text-muted">
                    Give owners, managers, captains, and charter teams a system that feels organized, credible, and built for growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="porto-section porto-story">
    <div class="container">
      <div class="row align-items-center g-5">
        <div class="col-lg-5">
          <span class="porto-eyebrow text-primary">Why serious operators switch</span>
          <h2 class="display-6 fw-bold mt-3 mb-3">The difference is not just better software. It is better operating discipline.</h2>
          <p class="text-muted mb-4">
            When maintenance history, route planning, reminders, inventory, checklists, and documentation finally live inside one operational layer,
            your team stops chasing information and starts moving with intent. That shift protects vessel value, improves guest delivery, and makes each season easier to run.
          </p>
          <a routerLink="/features" class="btn btn-outline-primary porto-btn px-4">Explore the full feature set</a>
        </div>
        <div class="col-lg-7">
          <div class="row g-4">
            <div class="col-md-6">
              <article class="porto-info-card h-100">
                <div class="icon-box-circle mb-3">
                  <i class="bi bi-shield-check"></i>
                </div>
                <h3 class="h4">Protect asset value</h3>
                <p class="text-muted mb-0">
                  Keep service history complete, stay ahead of due work, and maintain a cleaner ownership record that supports long-term vessel quality.
                </p>
              </article>
            </div>
            <div class="col-md-6">
              <article class="porto-info-card h-100">
                <div class="icon-box-circle mb-3">
                  <i class="bi bi-stars"></i>
                </div>
                <h3 class="h4">Elevate the guest experience</h3>
                <p class="text-muted mb-0">
                  Charter teams can deliver smoother turnarounds, better preparation, and a more premium standard customers feel immediately.
                </p>
              </article>
            </div>
            <div class="col-md-6">
              <article class="porto-info-card h-100">
                <div class="icon-box-circle mb-3">
                  <i class="bi bi-people"></i>
                </div>
                <h3 class="h4">Strengthen accountability</h3>
                <p class="text-muted mb-0">
                  Clarify what has been done, what is due next, and who owns the follow-up across owners, skippers, crew, and service partners.
                </p>
              </article>
            </div>
            <div class="col-md-6">
              <article class="porto-info-card h-100">
                <div class="icon-box-circle mb-3">
                  <i class="bi bi-speedometer2"></i>
                </div>
                <h3 class="h4">Move faster with less stress</h3>
                <p class="text-muted mb-0">
                  Replace memory-based coordination and scattered spreadsheets with a dashboard built to support decisive daily execution.
                </p>
              </article>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="porto-section porto-dark-band">
    <div class="container">
      <div class="row g-4 text-center">
        <div class="col-md-3 col-6">
          <div class="counter-card">
            <div class="counter-number">1</div>
            <p class="mb-0">Connected platform for boats, cruises, worklogs, documents, and reminders.</p>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="counter-card">
            <div class="counter-number">24/7</div>
            <p class="mb-0">Access to your operational history and vessel context from anywhere.</p>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="counter-card">
            <div class="counter-number">100%</div>
            <p class="mb-0">Clearer visibility into overdue work, readiness gaps, and operational follow-through.</p>
          </div>
        </div>
        <div class="col-md-3 col-6">
          <div class="counter-card">
            <div class="counter-number">0</div>
            <p class="mb-0">Tolerance for preventable chaos, lost records, or last-minute scrambling.</p>
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="porto-section porto-audience">
    <div class="container">
      <div class="text-center mb-5">
        <span class="porto-eyebrow text-primary">Built for your exact reality</span>
        <h2 class="display-6 fw-bold mt-3 mb-3">Two audiences. One stronger standard of control.</h2>
        <p class="text-muted lead mb-0 porto-narrow-copy mx-auto">
          Whether you are protecting a personally owned yacht or building a more dependable charter operation,
          {{ title }} helps you run with greater confidence, consistency, and professional polish.
        </p>
      </div>

      <div class="row g-4">
        <div class="col-lg-6">
          <article class="audience-card h-100">
            <span class="audience-label">For private boat owners</span>
            <h3 class="mt-3 mb-3">Enjoy ownership with less mental load and more certainty.</h3>
            <p class="text-muted">
              Keep maintenance schedules, service records, documents, routes, and trip preparation under control without turning boat ownership into an administrative burden.
              See how <a routerLink="/smart-yacht-management">smart yacht management</a> brings these daily tasks into one workflow.
            </p>
            <ul class="porto-list">
              <li>Know exactly what your boat needs next and when it needs it.</li>
              <li>Preserve a trustworthy record of upkeep, repairs, and operational history.</li>
              <li>Spend more time enjoying the water and less time reconstructing details from memory.</li>
            </ul>
          </article>
        </div>
        <div class="col-lg-6">
          <article class="audience-card h-100 accent-card">
            <span class="audience-label">For mid-level charter businesses</span>
            <h3 class="mt-3 mb-3">Run cleaner turnarounds, tighter coordination, and a more premium service rhythm.</h3>
            <p class="text-muted">
              Build repeatable workflows that support readiness, accountability, and a guest-facing experience worthy of the boats you operate.
              Explore <a routerLink="/yachtapp-solutions">yacht operations solutions for owners and crews</a> for maintenance, handovers and charter turnarounds.
            </p>
            <ul class="porto-list">
              <li>Standardize vessel prep, maintenance follow-up, and voyage coordination.</li>
              <li>Give managers, skippers, and crew a shared source of truth that reduces friction.</li>
              <li>Scale operations with confidence instead of adding more spreadsheets and more risk.</li>
            </ul>
          </article>
        </div>
      </div>
    </div>
  </section>

  <section class="porto-section porto-process">
    <div class="container">
      <div class="text-center mb-5">
        <span class="porto-eyebrow text-primary">A simpler operating rhythm</span>
        <h2 class="display-6 fw-bold mt-3 mb-3">From setup to daily execution in four practical moves.</h2>
      </div>
      <div class="row g-4">
        <div class="col-md-6 col-xl-3">
          <article class="process-card h-100">
            <div class="process-step">01</div>
            <h3 class="h4">Add your vessels</h3>
            <p class="text-muted mb-0">Create a structured home for each boat with the details, status, and records your operation depends on.</p>
          </article>
        </div>
        <div class="col-md-6 col-xl-3">
          <article class="process-card h-100">
            <div class="process-step">02</div>
            <h3 class="h4">Organize maintenance</h3>
            <p class="text-muted mb-0">Turn recurring upkeep, repairs, and service follow-up into visible tasks with history and accountability.</p>
          </article>
        </div>
        <div class="col-md-6 col-xl-3">
          <article class="process-card h-100">
            <div class="process-step">03</div>
            <h3 class="h4">Coordinate cruises</h3>
            <p class="text-muted mb-0">Plan routes, people, reminders, and readiness steps together so voyages start from a position of control.</p>
          </article>
        </div>
        <div class="col-md-6 col-xl-3">
          <article class="process-card h-100">
            <div class="process-step">04</div>
            <h3 class="h4">Operate with confidence</h3>
            <p class="text-muted mb-0">Use one platform to keep your season organized, your standards visible, and your team moving in the same direction.</p>
          </article>
        </div>
      </div>
    </div>
  </section>

  <section class="porto-section porto-testimonial-wrap">
    <div class="container">
      <div class="testimonial-card mx-auto text-center">
        <span class="porto-eyebrow text-primary">What the best upgrade really feels like</span>
        <blockquote class="testimonial-quote mt-3 mb-4">
          “The strongest operations are not the ones working harder to hold things together. They are the ones running on a system that makes excellence repeatable.”
        </blockquote>
        <p class="text-muted mb-0 porto-narrow-copy mx-auto">
          That is exactly where {{ title }} is built to take you: away from reactive coordination and toward a standard of ownership and charter execution that feels calm, capable, and unmistakably professional.
        </p>
      </div>
    </div>
  </section>

  <section class="porto-section porto-cta">
    <div class="container">
      <div class="cta-panel text-center">
        <span class="porto-eyebrow text-primary">Make this your next operational upgrade</span>
        <h2 class="display-6 fw-bold mt-3 mb-3">Your boats deserve more than scattered tools. Your business deserves more than reactive workflows.</h2>
        <p class="lead text-muted mb-4 porto-narrow-copy mx-auto">
          Start now and move into a platform designed to protect vessel quality, strengthen day-to-day control,
          and help private owners and charter teams operate at a level that feels premium from every angle.
        </p>
        <div class="d-flex flex-column flex-sm-row justify-content-center gap-3">
          <a routerLink="/register" class="btn btn-primary btn-lg porto-btn px-5">Create your account</a>
          <a routerLink="/subscriptions" class="btn btn-outline-primary btn-lg porto-btn px-5">See available plans</a>
        </div>
      </div>
    </div>
  </section>
</div>
